import Media from '@/components/shared/Media/Media';
import { ArrowRight } from '@1debit/icons';
import RichTextHeadline from 'components/shared/RichTextHeadline/RichTextHeadline';
import { RelatedArticleProps } from './types';
import Link from 'components/shared/Link/Link';
import { ArticleTag } from './ArticleTag';
import { getRichTextValue, formatDate } from 'utils/formatters';
import { liveUpdateAttrs } from 'libs/contentful/utils';
import { cn, getColorway } from 'utils/styles';
import { Colorways } from 'types/constants';

export const MediumArticleDisplayVariant = {
  Default: 'default',
  Card: 'card',
} as const;
export type MediumArticleDisplayVariant =
  (typeof MediumArticleDisplayVariant)[keyof typeof MediumArticleDisplayVariant];

const DISPLAY_VARIANT_STYLES = {
  [MediumArticleDisplayVariant.Default]: {
    container: '',
    text: '',
    image: '',
  },
  [MediumArticleDisplayVariant.Card]: {
    container: 'rounded-lg overflow-hidden',
    text: 'p-6',
    image: 'rounded-t-lg rounded-b-none',
  },
} as const;

interface MediumArticleProps extends RelatedArticleProps {
  displayVariant?: MediumArticleDisplayVariant;
  colorway?: Colorways;
  showDates?: boolean;
  includeCategoryLinks?: boolean;
  publishDate?: string;
  className?: string;
}

const MediumArticle = ({
  articleImage,
  articleTitle,
  category,
  slug,
  sys,
  publishDate,
  showDates,
  includeCategoryLinks,
  displayVariant = MediumArticleDisplayVariant.Default,
  colorway,
  className,
}: MediumArticleProps) => {
  const categoryTitle = category?.label;
  const linkText = 'Read more';
  const plainTitle = getRichTextValue(articleTitle);
  let formattedDate;
  if (publishDate) {
    formattedDate = formatDate(publishDate);
  } else if (sys?.createdAt) {
    formattedDate = formatDate(sys?.createdAt);
  }

  const variantStyles = DISPLAY_VARIANT_STYLES[displayVariant];

  return (
    <div
      data-testid={'mediumArticle'}
      className={cn(
        'flex flex-1 flex-col gap-y-6',
        colorway && getColorway(colorway),
        variantStyles.container,
        className,
      )}>
      {articleImage && (
        <div className="h-50 sm:h-64 w-full" {...liveUpdateAttrs(sys?.id, 'articleImage')}>
          <Media
            media={articleImage}
            sizes={'(max-width: 750px) 100vw, 420px'}
            className={cn(variantStyles.image, 'object-cover h-full w-full')}
          />
        </div>
      )}
      <div className={cn(variantStyles.text, 'flex flex-col flex-1 space-y-2.5')}>
        {categoryTitle && <ArticleTag label={categoryTitle} href={includeCategoryLinks ? category?.url : undefined} />}
        <div className="font-sans space-y-8">
          <div className="text-heading-sm" {...liveUpdateAttrs(sys?.id, 'articleTitle')}>
            <Link internalPage={{ slug }}>
              <RichTextHeadline level="h3" headline={articleTitle} />
            </Link>
          </div>
          {showDates && formattedDate && (
            <div data-testid={'mediumArticleDate'} className="font-sans font-medium text-base -mt-5">
              <p>{formattedDate}</p>
            </div>
          )}
        </div>
        <div className="text-label-saans mt-auto">
          <Link internalPage={{ slug }} className={'hover:underline'} aria-label={`${linkText}: ${plainTitle}`}>
            {linkText} <ArrowRight className={'inline size-(--ck-icon-size)'} />
          </Link>
        </div>
      </div>
    </div>
  );
};

export default MediumArticle;
